import './styles/GeneralInfo.scss';

import { useQuery } from '@apollo/client';
import { HelpOutline } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
  Tooltip
} from '@mui/material';
import { Course } from '@thro-types/core';

import clsx from 'clsx';
import { Field, FieldProps, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import AUTHOR_ROLE_QUERY from 'gql/queries/EventService/getPermissions.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import StyledDropzone from 'app/components/Dropzone';
import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { ModuleSelection, ObserverType, SubjectType } from '../../models';
import { ObserverUserAutocomplete } from './ObserverUserAutocomplete';

export const GeneralInfo: React.FC = () => {
  const { t } = useTranslation();
  const { setFieldValue, values, errors, handleChange, touched, setFieldTouched, handleBlur } =
    useFormikContext<ModuleSelection>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getAuthorRolesQuery = useQuery(AUTHOR_ROLE_QUERY, { variables: { limit: 0 } });

  const userQuery = useQuery<any, any>(USER_QUERY, {
    context: { clientName: 'federation' }
  });

  const changeHandler = (field, text) => {
    setFieldValue(field, text);
  };

  const debouncedChangeHandler = useCallback(
    debounce((field, text) => {
      changeHandler(field, text);
    }, 500),
    []
  );
  return (
    <GQLGuard
      query={[userQuery, getAuthorRolesQuery]}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`));
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.modules.alerts.failedLoading())}: ${error.message}`
            )
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([userData, authorRoles]) => {
        const sortedCourses: Course[] = userData.currentUser?.courses
          .slice()
          .sort((a, b) => (a.shorthand.toLowerCase() > b.shorthand.toLowerCase() ? 1 : -1));
        const authorObserverOptions = authorRoles.permissions
          ?.map(role => ({
            label: role.authorRole.name,
            id: parseInt(role.authorRole.id, 10)
          }))
          .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
        return (
          <Box p={2} className="generalInfo">
            {values.subjectType !== SubjectType.Awpm && (
              <FormControl className="moduleModal__body__input" required fullWidth>
                <FormLabel className="moduleModal__body__input__label">
                  {t(translations.pages.modules.faculty())}
                </FormLabel>
                <TextField
                  select
                  defaultValue=""
                  id="courseId"
                  name="courseId"
                  value={values.courseId}
                  error={touched.courseId && Boolean(errors.courseId)}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                >
                  {sortedCourses.map(course => {
                    return (
                      <MenuItem value={course.id} key={course.id}>
                        {'(' + course.shorthand + ') ' + course.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
                {touched.courseId && errors.courseId && (
                  <FormHelperText error={!!(touched.courseId && errors.courseId)}>
                    {errors.courseId}
                  </FormHelperText>
                )}
              </FormControl>
            )}
            <FormControl required fullWidth>
              <FormLabel>
                {t(translations.pages.moduleSelections.create.generalInfo.title())}
              </FormLabel>
              <TextField
                name="title"
                id="title"
                variant="outlined"
                fullWidth
                required
                value={values.title}
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel required>
                {t(translations.pages.moduleSelections.create.generalInfo.generalNotes())}
              </FormLabel>
              <Field name="generalNotes" error={touched.generalNotes && errors.generalNotes}>
                {({ field }: FieldProps) => (
                  <>
                    <ReactQuill
                      className={clsx('moduleModal__body__input__rte', {
                        'moduleModal__body__input__rte--error':
                          touched.generalNotes && errors.generalNotes
                      })}
                      value={field.value}
                      onChange={(_content, _delta, _source, editor) =>
                        debouncedChangeHandler('generalNotes', editor.getHTML())
                      }
                      onBlur={(prev, source, editor) => {
                        setFieldTouched('generalNotes', true);
                      }}
                      theme="snow"
                      modules={{
                        toolbar: [
                          ['bold', 'italic'],
                          ['link'],
                          [
                            { list: 'ordered' },
                            { list: 'bullet' },
                            { indent: '-1' },
                            { indent: '+1' }
                          ],
                          ['clean']
                        ]
                      }}
                      formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
                    />

                    {touched.generalNotes && errors.generalNotes && (
                      <FormHelperText error>{errors.generalNotes}</FormHelperText>
                    )}
                  </>
                )}
              </Field>
            </FormControl>
            <FormControl fullWidth>
              <FormLabel>
                {t(translations.pages.moduleSelections.create.generalInfo.electionTip())}
              </FormLabel>
              <Field name="electionTip" error={touched.electionTip && errors.electionTip}>
                {({ field }: FieldProps) => (
                  <>
                    <ReactQuill
                      className={clsx('moduleModal__body__input__rte', {
                        'moduleModal__body__input__rte--error':
                          touched.electionTip && errors.electionTip
                      })}
                      value={field.value}
                      onChange={(_content, _delta, _source, editor) =>
                        debouncedChangeHandler('electionTip', editor.getHTML())
                      }
                      onBlur={(prev, source, editor) => {
                        setFieldTouched('electionTip', true);
                      }}
                      theme="snow"
                      modules={{
                        toolbar: [
                          ['bold', 'italic'],
                          ['link'],
                          [
                            { list: 'ordered' },
                            { list: 'bullet' },
                            { indent: '-1' },
                            { indent: '+1' }
                          ],
                          ['clean']
                        ]
                      }}
                      formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
                    />
                    {touched.electionTip && errors.electionTip && (
                      <FormHelperText error>{errors.electionTip}</FormHelperText>
                    )}
                  </>
                )}
              </Field>
            </FormControl>
            <FormControl fullWidth>
              <FormLabel>
                {t(translations.pages.moduleSelections.create.generalInfo.attachments())}
              </FormLabel>
              <StyledDropzone
                name="attachments"
                setFieldValue={setFieldValue}
                value={values.attachments}
                errors={errors}
                placeholder={t(
                  translations.pages.modules.create.GeneralInfo.attachmentPlaceholder()
                )}
                multiple
              />
            </FormControl>
            <Grid container gap={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Grid container>
                    <FormLabel>
                      {t(translations.pages.moduleSelections.create.generalInfo.viewer())}
                    </FormLabel>
                    <Grid
                      item
                      container
                      xs
                      justifyContent="flex-end"
                      alignItems="self-end"
                      paddingBottom={'0.5em'}
                    >
                      <Tooltip
                        title={t(
                          translations.pages.moduleSelections.create.generalInfo.tooltips.viewingRoles()
                        )}
                      >
                        <HelpOutline fontSize="medium" />
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Autocomplete
                    value={
                      (values.observers &&
                        values.observers
                          ?.filter(org => org.observerType !== ObserverType.USER)
                          .map(org => authorObserverOptions.find(opt => opt.id === org.refId))) ||
                      []
                    }
                    multiple
                    id="observers"
                    options={authorObserverOptions}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    fullWidth
                    onChange={(_, value) => {
                      setFieldValue('observers', [
                        ...values.observers?.filter(org => org.observerType !== ObserverType.ROLE),
                        ...value.map(v => ({
                          refId: v.id,
                          observerType: ObserverType.ROLE
                        }))
                      ]);
                    }}
                    renderInput={params => <TextField {...params} />}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel>
                    {t(translations.pages.moduleSelections.create.generalInfo.organizingPersons())}
                  </FormLabel>
                  <ObserverUserAutocomplete />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      }}
    </GQLGuard>
  );
};
